/**
 * Vizard API Service
 * Handles all API calls to the backend for Vizard.ai functionality
 */

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';
const VIZARD_BASE = `${API_BASE}/api/vizard`;

/**
 * Submit a video for processing
 * @param {Object} videoData - Video submission data
 * @param {string} videoData.videoUrl - URL of the video
 * @param {number} videoData.videoType - Type of video source (1=direct, 2=YouTube, etc.)
 * @param {string} videoData.userId - User ID
 * @param {string} videoData.episodeId - Episode ID
 * @param {Object} videoData.options - Processing options
 * @returns {Promise<{success: boolean, jobId: string, status: string}>}
 */
export const submitVideo = async (videoData) => {
    try {
        const response = await fetch(`${VIZARD_BASE}/submit`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(videoData)
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.error || 'Failed to submit video');
        }

        return data;
    } catch (error) {
        console.error('Error submitting video:', error);
        throw error;
    }
};

/**
 * Poll the status of a processing job
 * @param {string} jobId - The job ID to check
 * @param {string} episodeId - The episode ID
 * @returns {Promise<{success: boolean, status: string, progress: number}>}
 */
export const pollStatus = async (jobId, episodeId) => {
    try {
        const response = await fetch(
            `${VIZARD_BASE}/status/${jobId}?episodeId=${episodeId}`
        );

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.error || 'Failed to get job status');
        }

        return data;
    } catch (error) {
        console.error('Error polling status:', error);
        throw error;
    }
};

/**
 * Retrieve generated clips for a completed job
 * @param {string} jobId - The job ID
 * @param {string} episodeId - The episode ID
 * @returns {Promise<{success: boolean, clips: Array}>}
 */
export const getClips = async (jobId, episodeId) => {
    try {
        const response = await fetch(
            `${VIZARD_BASE}/clips/${jobId}?episodeId=${episodeId}`
        );

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.error || 'Failed to get clips');
        }

        return data;
    } catch (error) {
        console.error('Error getting clips:', error);
        throw error;
    }
};

/**
 * Delete a job and its associated clips
 * @param {string} jobId - The job ID to delete
 * @param {string} episodeId - The episode ID
 * @returns {Promise<{success: boolean}>}
 */
export const deleteJob = async (jobId, episodeId) => {
    try {
        const response = await fetch(
            `${VIZARD_BASE}/jobs/${jobId}?episodeId=${episodeId}`,
            {
                method: 'DELETE'
            }
        );

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.error || 'Failed to delete job');
        }

        return data;
    } catch (error) {
        console.error('Error deleting job:', error);
        throw error;
    }
};

/**
 * Delete a specific clip
 * @param {string} clipId - The clip ID to delete
 * @param {string} episodeId - The episode ID
 * @returns {Promise<{success: boolean}>}
 */
export const deleteClip = async (clipId, episodeId) => {
    try {
        const response = await fetch(
            `${VIZARD_BASE}/clips/${clipId}?episodeId=${episodeId}`,
            {
                method: 'DELETE'
            }
        );

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.error || 'Failed to delete clip');
        }

        return data;
    } catch (error) {
        console.error('Error deleting clip:', error);
        throw error;
    }
};

/**
 * Generate AI captions for a clip
 * @param {string} clipId - The clip ID
 * @param {string} episodeId - The episode ID
 * @param {Object} options - Caption generation options
 * @returns {Promise<{success: boolean, caption: string, hashtags: Array}>}
 */
export const generateCaption = async (clipId, episodeId, options = {}) => {
    try {
        const response = await fetch(`${VIZARD_BASE}/captions/${clipId}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ episodeId, options })
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.error || 'Failed to generate caption');
        }

        return data;
    } catch (error) {
        console.error('Error generating caption:', error);
        throw error;
    }
};

/**
 * Mark a clip as saved
 * @param {string} clipId - The clip ID
 * @param {string} episodeId - The episode ID
 * @param {string} userId - The user ID
 * @returns {Promise<{success: boolean}>}
 */
export const saveClip = async (clipId, episodeId, userId) => {
    try {
        const response = await fetch(`${VIZARD_BASE}/clips/${clipId}/save`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ episodeId, userId })
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.error || 'Failed to save clip');
        }

        return data;
    } catch (error) {
        console.error('Error saving clip:', error);
        throw error;
    }
};

/**
 * Mark a clip as published to a platform
 * @param {string} clipId - The clip ID
 * @param {string} episodeId - The episode ID
 * @param {string} platform - The platform name
 * @returns {Promise<{success: boolean}>}
 */
export const publishClip = async (clipId, episodeId, platform) => {
    try {
        const response = await fetch(`${VIZARD_BASE}/clips/${clipId}/publish`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ episodeId, platform })
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.error || 'Failed to publish clip');
        }

        return data;
    } catch (error) {
        console.error('Error publishing clip:', error);
        throw error;
    }
};

/**
 * Upload a video file to Firebase Storage and get a URL
 * This is a helper function for file uploads before submitting to Vizard
 * @param {File} file - The video file to upload
 * @param {string} episodeId - The episode ID
 * @returns {Promise<{url: string}>}
 */
export const uploadVideoFile = async (file, episodeId) => {
    try {
        const formData = new FormData();
        formData.append('video', file);
        formData.append('episodeId', episodeId);

        const response = await fetch(`${API_BASE}/api/uploads/video`, {
            method: 'POST',
            body: formData
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.error || 'Failed to upload video');
        }

        return data;
    } catch (error) {
        console.error('Error uploading video file:', error);
        throw error;
    }
};
